import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowRight, Dumbbell, Info, ImageIcon } from "lucide-react";
import { useAssessment } from "@/context/AssessmentContext";
import { MUSCLE_TEST_IMG, MUSCLE_TEST_STEPS } from "@/lib/posturePatterns";

// The 3 activities shown in every muscle test diagram (matches image panels 1/2/3)
const ACTIVITIES = [
  {
    key: "strength",
    label: "Strength hold",
    hint: "Panel 1 · Push against light resistance for 5–10 seconds.",
    color: "#E07A5F",
  },
  {
    key: "tightness",
    label: "Stretch",
    hint: "Panel 2 · Gently stretch the muscle to end-range.",
    color: "#F2CC8F",
  },
  {
    key: "spasm",
    label: "Palpation point",
    hint: "Panel 3 · Press gently on the marked spot to find a knot.",
    color: "#52796F",
  },
];

const SEVERITY = [
  { key: "none", label: "Nothing", sub: "no pain, felt fine" },
  { key: "mild", label: "Slight", sub: "mild discomfort" },
  { key: "painful", label: "Painful", sub: "sharp / bad" },
];

export default function MuscularAssessment() {
  const { data, update } = useAssessment();
  const navigate = useNavigate();
  const pattern = data.pattern;

  const targets = useMemo(() => {
    const groups = [];
    if (pattern?.tight_muscles) groups.push(...pattern.tight_muscles.map((m) => ({ ...m, group: "tight" })));
    if (pattern?.weak_muscles) groups.push(...pattern.weak_muscles.map((m) => ({ ...m, group: "weak" })));
    return groups;
  }, [pattern]);

  const [results, setResults] = useState({});
  const [side, setSide] = useState("bilateral");

  if (!pattern) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground mb-4">No posture picked yet.</p>
        <Button onClick={() => navigate("/posture-pattern")} className="bg-forest hover:bg-forest/90 text-bone">Go back</Button>
      </div>
    );
  }

  const setResult = (muscle, sideKey, activityKey, severity) => {
    setResults((r) => ({
      ...r,
      [muscle]: {
        ...(r[muscle] || {}),
        [sideKey]: { ...(r[muscle]?.[sideKey] || {}), [activityKey]: severity },
      },
    }));
  };

  const proceed = () => {
    update("muscular", { pattern_id: pattern.id, results });
    navigate("/report");
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-2 mb-4 text-sm text-forest font-medium">
        <Dumbbell className="w-4 h-4" /> Step 4 · Check Your Muscles
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest mb-3">
        Which activities cause pain or discomfort?
      </h1>
      <p className="text-muted-foreground mb-6 max-w-3xl">
        For each muscle involved in your <span className="font-medium text-forest">{pattern.plain_name || pattern.name}</span>,
        try the three positions shown in the picture. Tell us how each one felt.
      </p>

      <Card className="p-4 mb-6 bg-honey/10 border-honey/40 flex items-start gap-3">
        <Info className="w-5 h-5 text-forest shrink-0 mt-0.5" />
        <div className="text-sm text-ink">
          <p className="font-medium mb-1">How to answer:</p>
          <p className="text-muted-foreground">
            Look at each of the three panels in the picture and try that position on yourself. Then tap
            <span className="text-forest font-medium"> Nothing</span>,
            <span className="text-honey font-medium"> Slight</span>, or
            <span className="text-terracotta font-medium"> Painful</span> for each one.
          </p>
        </div>
      </Card>

      <div className="flex gap-2 mb-6">
        {[
          { key: "bilateral", label: "Both sides" },
          { key: "left", label: "Left only" },
          { key: "right", label: "Right only" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setSide(s.key)}
            data-testid={`side-${s.key}`}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
              side === s.key ? "bg-forest text-bone border-forest" : "border-forest/20 hover:border-forest/40"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <Tabs defaultValue={targets[0]?.name} className="w-full">
        <TabsList className="flex-wrap h-auto bg-bone border border-forest/10 p-1">
          {targets.map((t) => (
            <TabsTrigger
              key={t.name}
              value={t.name}
              className="data-[state=active]:bg-forest data-[state=active]:text-bone"
              data-testid={`tab-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {t.plain || t.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {targets.map((t) => {
          const testImg = MUSCLE_TEST_IMG[t.name];
          const stepsForMuscle = MUSCLE_TEST_STEPS[t.name];
          const sides = side === "bilateral" ? ["bilateral"] : [side];
          return (
            <TabsContent key={t.name} value={t.name} className="mt-6">
              <Card className="overflow-hidden bg-white border-forest/10">
                {testImg ? (
                  <div className="w-full bg-[#F7F1EA] p-3 sm:p-4 border-b border-forest/10">
                    <img
                      src={testImg}
                      alt={`${t.name} test positions`}
                      className="w-full max-h-[420px] object-contain mx-auto"
                    />
                  </div>
                ) : (
                  <div className="w-full bg-bone p-8 border-b border-forest/10 flex items-center justify-center text-muted-foreground text-sm">
                    <ImageIcon className="w-5 h-5 mr-2" /> Test picture coming soon — follow the written cues below.
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <p
                        className="text-xs uppercase tracking-[0.2em] mb-1"
                        style={{ color: t.group === "tight" ? "#E07A5F" : "#52796F" }}
                      >
                        {t.group === "tight" ? "Usually too tight" : "Usually too weak"}
                      </p>
                      <h3 className="font-heading text-2xl font-semibold text-forest">{t.plain}</h3>
                      <p className="text-xs text-muted-foreground italic mt-1">Anatomical: {t.name}</p>
                    </div>
                  </div>

                  {/* Consolidated step-by-step instructions block */}
                  {stepsForMuscle && (
                    <div className="mb-6 rounded-lg border border-forest/15 overflow-hidden">
                      <div className="px-4 py-2 bg-forest text-bone text-xs uppercase tracking-[0.2em] font-medium">
                        Step-by-step · read all three before answering
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-forest/10">
                        {ACTIVITIES.map((a, i) => (
                          <div key={a.key} className="p-4 bg-bone/50">
                            <div className="flex items-center gap-2 mb-2">
                              <span
                                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                                style={{ background: a.color }}
                              >
                                {i + 1}
                              </span>
                              <p className="font-heading text-sm font-semibold text-forest uppercase tracking-wide">
                                {a.label}
                              </p>
                            </div>
                            <p className="text-sm text-ink leading-relaxed">
                              {stepsForMuscle[a.key] || a.hint}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 mt-6">
                    Now rate each part
                  </p>

                  {sides.map((sd) => (
                    <div key={sd} className="mb-4 last:mb-0">
                      {side !== "bilateral" && (
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{sd} side</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {ACTIVITIES.map((a) => {
                          const val = results[t.name]?.[sd]?.[a.key];
                          return (
                            <div
                              key={a.key}
                              className="border border-forest/10 rounded-lg p-4"
                              style={{ borderTopColor: a.color, borderTopWidth: 3 }}
                            >
                              <p className="font-heading text-sm font-semibold text-forest mb-3">{a.label}</p>
                              <div className="grid grid-cols-3 gap-1.5">
                                {SEVERITY.map((s) => {
                                  const active = val === s.key;
                                  const bg =
                                    s.key === "none" ? "#52796F" : s.key === "mild" ? "#F2CC8F" : "#E07A5F";
                                  const fg = s.key === "mild" ? "#1B4332" : "#FFF";
                                  return (
                                    <button
                                      key={s.key}
                                      onClick={() => setResult(t.name, sd, a.key, s.key)}
                                      data-testid={`sev-${t.name}-${a.key}-${sd}-${s.key}`}
                                      className={`px-2 py-2 rounded-md text-xs border transition-all ${
                                        active
                                          ? "border-transparent shadow-sm"
                                          : "border-forest/15 hover:border-forest/40 bg-white"
                                      }`}
                                      style={active ? { background: bg, color: fg } : {}}
                                    >
                                      <div className="font-medium">{s.label}</div>
                                      <div className="text-[10px] opacity-80 leading-tight mt-0.5">{s.sub}</div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      <div className="flex justify-end mt-8">
        <Button
          onClick={proceed}
          className="bg-forest hover:bg-forest/90 text-bone rounded-full px-7"
          data-testid="muscular-continue-btn"
        >
          Get my plan <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
